import { TemplateState } from '../ngrx/template/template.reducers';

export interface PersistedTemplateState {
  currentMode: 1 | 2 | 3;
  slugs: {
    [key: string]: {
      staticTemplate: any;
      dynamicTemplate: any;
      customSlug: any;
      loaded: boolean;
      timestamp: number;
    }
  };
  version: string;
  lastUpdated: number;
}

export class StatePersistenceUtil {
  private static readonly STORAGE_KEY = 'duna_template_state';
  private static readonly CACHE_VERSION = '2.0';
  private static readonly MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  private static readonly MAX_STORAGE_SIZE = 10 * 1024 * 1024; // 10MB limit

  /**
   * Save template state to localStorage with compression and versioning
   */
  static saveTemplateState(state: TemplateState): void {
    try {
      const persistedState: PersistedTemplateState = {
        currentMode: state.currentMode,
        slugs: {},
        version: this.CACHE_VERSION,
        lastUpdated: Date.now()
      };

      // Only persist successfully loaded and not-too-old data
      const now = Date.now();
      Object.entries(state.slugs).forEach(([slug, slugData]) => {
        if (slugData.loaded && (slugData.staticTemplate || slugData.dynamicTemplate || slugData.customSlug)) {
          persistedState.slugs[slug] = {
            staticTemplate: slugData.staticTemplate,
            dynamicTemplate: slugData.dynamicTemplate,
            customSlug: slugData.customSlug,
            loaded: true,
            timestamp: now
          };
        }
      });

      const serialized = JSON.stringify(persistedState);
      
      // Check storage size limit
      if (serialized.length > this.MAX_STORAGE_SIZE) {
        console.warn('[StatePersistence] State too large, pruning old entries');
        this.pruneOldEntries(persistedState);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(persistedState));
    } catch (error) {
      console.error('[StatePersistence] Error saving template state:', error);
      // If storage is full, try to clear old data
      this.clearExpiredData();
    }
  }

  /**
   * Load template state from localStorage with validation and cleanup
   */
  static loadTemplateState(): Partial<TemplateState> | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const persistedState: PersistedTemplateState = JSON.parse(stored);
      
      // Version check
      if (persistedState.version !== this.CACHE_VERSION) {
        this.clearStoredState();
        return null;
      }

      // Age check
      const now = Date.now();
      if (now - persistedState.lastUpdated > this.MAX_AGE_MS) {
        this.clearStoredState();
        return null;
      }

      // Filter out expired entries
      const validSlugs: any = {};
      Object.entries(persistedState.slugs).forEach(([slug, slugData]) => {
        if (now - slugData.timestamp < this.MAX_AGE_MS) {
          validSlugs[slug] = {
            staticTemplate: slugData.staticTemplate,
            dynamicTemplate: slugData.dynamicTemplate,
            customSlug: slugData.customSlug,
            loaded: true,
            lazyLoaded: false,
            lazyLoadedData: null
          };
        }
      });

      return {
        currentMode: persistedState.currentMode,
        slugs: validSlugs
      };
    } catch (error) {
      console.error('[StatePersistence] Error loading template state:', error);
      this.clearStoredState();
      return null;
    }
  }

  /**
   * Check if a specific slug is cached and still valid
   */
  static isSlugCached(slug: string): boolean {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return false;

      const persistedState: PersistedTemplateState = JSON.parse(stored);
      const slugData = persistedState.slugs[slug];
      
      if (!slugData) return false;

      const now = Date.now();
      return now - slugData.timestamp < this.MAX_AGE_MS;
    } catch {
      return false;
    }
  }

  /**
   * Get cached template data for a specific slug
   */
  static getCachedSlug(slug: string): any {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;

      const persistedState: PersistedTemplateState = JSON.parse(stored);
      const slugData = persistedState.slugs[slug];
      
      if (!slugData) return null;

      const now = Date.now();
      if (now - slugData.timestamp > this.MAX_AGE_MS) {
        return null;
      }

      return {
        staticTemplate: slugData.staticTemplate,
        dynamicTemplate: slugData.dynamicTemplate,
        customSlug: slugData.customSlug,
        loaded: true,
        lazyLoaded: false,
        lazyLoadedData: null
      };
    } catch {
      return null;
    }
  }

  /**
   * Prune old entries to reduce storage size
   */
  private static pruneOldEntries(state: PersistedTemplateState): void {
    const entries = Object.entries(state.slugs);
    entries.sort((a, b) => b[1].timestamp - a[1].timestamp); // Sort by newest first
    
    // Keep only the newest 50 entries
    const maxEntries = 50;
    if (entries.length > maxEntries) {
      const keptEntries = entries.slice(0, maxEntries);
      state.slugs = {};
      keptEntries.forEach(([slug, data]) => {
        state.slugs[slug] = data;
      });
    }
  }

  /**
   * Clear expired data from localStorage
   */
  static clearExpiredData(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const persistedState: PersistedTemplateState = JSON.parse(stored);
      const now = Date.now();
      const validSlugs: any = {};

      Object.entries(persistedState.slugs).forEach(([slug, slugData]) => {
        if (now - slugData.timestamp < this.MAX_AGE_MS) {
          validSlugs[slug] = slugData;
        }
      });

      persistedState.slugs = validSlugs;
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(persistedState));
    } catch (error) {
      console.error('[StatePersistence] Error clearing expired data:', error);
      this.clearStoredState();
    }
  }

  /**
   * Clear all stored state
   */
  static clearStoredState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('[StatePersistence] Error clearing stored state:', error);
    }
  }

  /**
   * Get storage info for debugging
   */
  static getStorageInfo(): any {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return { exists: false };
      }

      const persistedState: PersistedTemplateState = JSON.parse(stored);
      const now = Date.now();
      const slugCount = Object.keys(persistedState.slugs).length;
      const validSlugs = Object.values(persistedState.slugs).filter(
        slug => now - slug.timestamp < this.MAX_AGE_MS
      ).length;

      return {
        exists: true,
        version: persistedState.version,
        lastUpdated: new Date(persistedState.lastUpdated).toISOString(),
        totalSlugs: slugCount,
        validSlugs,
        expiredSlugs: slugCount - validSlugs,
        sizeBytes: stored.length,
        ageHours: Math.round((now - persistedState.lastUpdated) / (1000 * 60 * 60))
      };
    } catch {
      return { exists: false, error: true };
    }
  }
} 